import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Alert, Button, Badge } from 'reactstrap';
import dateFnsParseISO from 'date-fns/parseISO';
import dateFnsFormat from 'date-fns/format';
import { tokenStorage } from 'Modules/API';
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';

const Connections = () => {
  const {
    t
  } = useTranslation();
  const [activeConnections, setActiveConnections] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [incomingInvitations, setIncomingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const token = tokenStorage.getAuthToken();

      if (!token) {
        setError('Not logged in');
        return;
      } // Fetch sent connections


      const sentResponse = await fetch(`${API_BASE}/users/connections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }); // Fetch incoming connections

      const incomingResponse = await fetch(`${API_BASE}/invitations/incoming`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!sentResponse.ok) {
        const errData = await sentResponse.json().catch(() => ({}));
        console.error("Sent connections error:", errData);
        const msg = errData && errData.error ? errData.error.message : sentResponse.statusText;
        throw new Error(`HTTP ${sentResponse.status}: ${msg}`);
      }

      if (!incomingResponse.ok) {
        const errData = await incomingResponse.json().catch(() => ({}));
        console.error("Incoming connections error:", errData);
        const msg = errData && errData.error ? errData.error.message : incomingResponse.statusText;
        throw new Error(`HTTP ${incomingResponse.status}: ${msg}`);
      }

      const sentData = await sentResponse.json();
      const incomingData = await incomingResponse.json();
      let allConnections = [];

      if (sentData.success && sentData.data && Array.isArray(sentData.data.connections)) {
        allConnections = sentData.data.connections;
      } else if (sentData.success && Array.isArray(sentData.data)) {
        allConnections = sentData.data;
      } // Split into active, sent, and incoming


      const active = allConnections.filter(c => c.status === 'connected' || c.status === 'accepted');
      const sent = allConnections.filter(c => (c.status === 'pending' || c.type === 'invitation') && c.direction === 'sent'); // For incoming, we prefer the /api/invitations/incoming endpoint to ensure we get all pending incoming

      let incoming = [];

      if (incomingData.success && Array.isArray(incomingData.data)) {
        incoming = incomingData.data.filter(c => c.status === 'pending');
      }

      setActiveConnections(active);
      setSentInvitations(sent);
      setIncomingInvitations(incoming);
    } catch (err) {
      console.error('Error fetching connections:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = dateString => {
    try {
      const isodate = dateFnsParseISO(dateString);
      return dateFnsFormat(isodate, 'PP');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusBadge = status => {
    const statusConfig = {
      'pending': {
        color: 'warning',
        text: 'Pending'
      },
      'connected': {
        color: 'success',
        text: 'Connected'
      },
      'declined': {
        color: 'danger',
        text: 'Declined'
      },
      'expired': {
        color: 'secondary',
        text: 'Expired'
      }
    };
    const config = statusConfig[status] || {
      color: 'info',
      text: status
    };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const getDisplayName = connection => {
    if (connection.type === 'invitation') {
      return connection.direction === 'sent' ? connection.toEmail || 'Unknown' : connection.fromEmail || 'Unknown';
    } // For actual connections, use firstName/lastName if available


    if (connection.firstName || connection.lastName) {
      return `${connection.firstName || ''} ${connection.lastName || ''}`.trim();
    } // Fallback to username/email


    if (connection.direction === 'sent') {
      return connection.toUsername || connection.toEmail || 'Unknown';
    } else {
      return connection.fromUsername || connection.fromEmail || 'Unknown';
    }
  };

  const deleteConnection = async connectionId => {
    if (!window.confirm('Are you sure you want to delete this connection? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingId(connectionId);
      const token = tokenStorage.getAuthToken();

      if (!token) {
        setError('Not logged in');
        return;
      }

      const response = await fetch(`${API_BASE}/connections/${connectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        const msg = errorData && errorData.error ? errorData.error.message : `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(msg);
      }

      const result = await response.json();

      if (result.success) {
        // Remove the connection from all lists
        setActiveConnections(prev => Array.isArray(prev) ? prev.filter(conn => conn.id !== connectionId) : []);
        setSentInvitations(prev => Array.isArray(prev) ? prev.filter(conn => conn.id !== connectionId) : []);
        setIncomingInvitations(prev => Array.isArray(prev) ? prev.filter(conn => conn.id !== connectionId) : []);
      } else {
        const resultMsg = result && result.error ? result.error.message : 'Failed to delete connection';
        throw new Error(resultMsg);
      }
    } catch (err) {
      console.error('Error deleting connection:', err);
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="text-center py-4">
                <Spinner color="primary" />
                <p className="mt-2">{t("Loading connections...")}</p>
            </div>;
  }

  if (error) {
    return <Alert color="danger">
                <h4>{t("Error")}</h4>
                <p>{error}</p>
                <Button color="primary" onClick={fetchConnections}>{t("Retry")}</Button>
            </Alert>;
  }

  return <Container className="mt-4">
            <Row>
                <Col>
                    <Card>

                        <div className="card-body">
                            <Row className="mb-4">
                                <Col>
                                    <h5>{t("Active Connections")}</h5>
                                    {!Array.isArray(activeConnections) || activeConnections.length === 0 ? <p className="text-muted">{t("No active connections.")}</p> : <Table responsive striped size="sm">
                                            <thead>
                                                <tr>
                                                    <th>{t("Name")}</th>
                                                    <th>{t("Status")}</th>
                                                    <th>{t("Connected Since")}</th>
                                                    <th>{t("Actions")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(Array.isArray(activeConnections) ? activeConnections : []).map(connection => <tr key={connection.id}>
                                                        <td>
                                                            <strong>{getDisplayName(connection)}</strong>
                                                            <br />
                                                            <small className="text-muted">{t("User connection")}</small>
                                                        </td>
                                                        <td>{getStatusBadge(connection.status)}</td>
                                                        <td>
                                                            <small>{formatDate(connection.createdAt)}</small>
                                                        </td>
                                                        <td>
                                                            <Button color="danger" size="sm" disabled={deletingId === connection.id} onClick={() => deleteConnection(connection.id)}>
                                                                {deletingId === connection.id ? <Spinner size="sm" /> : '🗑️ Remove'}
                                                            </Button>
                                                        </td>
                                                    </tr>)}
                                            </tbody>
                                        </Table>}
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col md="6">
                                    <h5>{t("Sent Invitations")}</h5>
                                    {!Array.isArray(sentInvitations) || sentInvitations.length === 0 ? <p className="text-muted">{t("No sent invitations.")}</p> : <Table responsive striped size="sm">
                                            <thead>
                                                <tr>
                                                    <th>{t("Recipient")}</th>
                                                    <th>{t("Status")}</th>
                                                    <th>{t("Sent")}</th>
                                                    <th>{t("Actions")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(Array.isArray(sentInvitations) ? sentInvitations : []).map(connection => <tr key={connection.id}>
                                                        <td>
                                                            <strong>{getDisplayName(connection)}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                {connection.type === 'invitation' ? 'Email invitation' : 'User connection'}
                                                            </small>
                                                        </td>
                                                        <td>{getStatusBadge(connection.status)}</td>
                                                        <td>
                                                            <small>{formatDate(connection.createdAt)}</small>
                                                        </td>
                                                        <td>
                                                            <Button color="danger" size="sm" disabled={deletingId === connection.id} onClick={() => deleteConnection(connection.id)}>
                                                                {deletingId === connection.id ? <Spinner size="sm" /> : '🗑️ Cancel'}
                                                            </Button>
                                                        </td>
                                                    </tr>)}
                                            </tbody>
                                        </Table>}
                                </Col>
                                <Col md="6">
                                    <h5>{t("Incoming Invitations")}</h5>
                                    {!Array.isArray(incomingInvitations) || incomingInvitations.length === 0 ? <p className="text-muted">{t("No incoming invitations.")}</p> : <Table responsive striped size="sm">
                                            <thead>
                                                <tr>
                                                    <th>{t("Sender")}</th>
                                                    <th>{t("Status")}</th>
                                                    <th>{t("Received")}</th>
                                                    <th>{t("Actions")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(Array.isArray(incomingInvitations) ? incomingInvitations : []).map(connection => <tr key={connection.id}>
                                                        <td>
                                                            <strong>{getDisplayName(connection)}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                {connection.type === 'invitation' ? 'Email invitation' : 'User connection'}
                                                            </small>
                                                        </td>
                                                        <td>{getStatusBadge(connection.status)}</td>
                                                        <td>
                                                            <small>{formatDate(connection.createdAt)}</small>
                                                        </td>
                                                        <td>
                                                            <Button color="danger" size="sm" disabled={deletingId === connection.id} onClick={() => deleteConnection(connection.id)}>
                                                                {deletingId === connection.id ? <Spinner size="sm" /> : '🗑️ Delete'}
                                                            </Button>
                                                        </td>
                                                    </tr>)}
                                            </tbody>
                                        </Table>}
                                </Col>
                            </Row>
                            <hr />
                            <div className="text-center">
                                <p className="text-muted">
                                    <small>{t("Use our mobile app to manage connections and send invitations.")}</small>
                                </p>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>
        </Container>;
};

export default Connections;